import { FileType } from 'shared/enums/FileType';
import { useDebounce } from 'use-debounce';
import { useEditProfileMediaDocumentData } from './useEditProfileMediaDocumentData';
import { mediaService } from 'shared/services/mediaService';
import { representativeService } from 'shared/services/representativesService';
import { talentService } from 'shared/services/talentService';
import { useMemo, useState, useEffect } from 'react';
import { IRepresentativesProfile } from 'shared/interfaces/IRepresentatives';

const { getMediaList, getMediaTags } = mediaService();
const { getSkills } = talentService();
const { getRepresentativesProfile } = representativeService();

export const useGetPhotoEditorData = (profileId: string) => {
  const [queryValue] = useDebounce('', 500);
  const { data: skillsData, isLoading: skillsDataLoading, isError: skillDataError } = getSkills();
  const {
    data: mediaData,
    isLoading: mediaDataLoading,
    isError: mediaDataError,
  } = getMediaList({ file_type: 'image', media_bin: true });
  const { data: mediaTags, isLoading: fetchingTags, isError: fetchingTagError } = getMediaTags(queryValue);

  const {
    data: multipleProfiles,
    isLoading: multipleProfilesLoading,
    isError: multipleProfilesError,
  } = getRepresentativesProfile();
  const { attached_media: attached_images } = useEditProfileMediaDocumentData(FileType.IMAGE);

  const checkImagePermission = (profile: IRepresentativesProfile) => {
    const use_global_permission =
      // If use own permission is false
      !profile?.talent_permissions ||
      // if the individual talent permission is null or undefined
      profile?.talent_permissions === null ||
      profile?.talent_permissions === undefined;

    // Permission
    const permissions: string[] =
      (use_global_permission ? profile?.agency?.talent_permissions : profile?.talent_permissions) || [];

    return permissions.includes('Images');
  };

  const agencies = useMemo(() => {
    return (
      multipleProfiles?.data?.profiles?.map((profile) => ({
        current: profile.id === profileId,
        division: profile.division?.name || '',
        id: profile.id,
        name: profile.agency?.full_name || 'Non-Represented',
        disabled: profile.agency?.full_name && !checkImagePermission(profile),
      })) || []
    );
  }, [multipleProfiles, profileId]);

  const [selectedAgencies, setSelectedAgencies] = useState<any>([]);

  useEffect(() => {
    if (agencies.length > 0) {
      setSelectedAgencies(agencies.filter((agency) => !agency.disabled));
    }
  }, [agencies]);

  const unselectedAgencies = useMemo(() => {
    return agencies.filter((agency) => !selectedAgencies.some((selected: any) => selected.id === agency.id));
  }, [agencies, selectedAgencies]);

  return {
    mediaData,
    mediaDataLoading,
    mediaDataError,
    skillsData,
    skillsDataLoading,
    skillDataError,
    mediaTags,
    fetchingTags,
    fetchingTagError,
    multipleProfilesLoading,
    multipleProfilesError,
    attached_images,
    selectedAgencies,
    unselectedAgencies,
    agencies,
    setSelectedAgencies,
  };
};
